/* ─── assets/awaken-product.js ─── */
class AwakenProduct {
  constructor(container) {
    this.container = container;
    const configEl = container.querySelector('.awaken-product-config');
    if (!configEl) return;
    
    this.config = JSON.parse(configEl.textContent);
    this.thumbIdx = this.config.visualStartIndex || 0;
    this.currentOptions = this.config.currentOptions;
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateBreakdownAndSpecs();
    this.initGallery();
    this.initTickers();
  }

  bindEvents() {
    const tabBtns = this.container.querySelectorAll('.awaken-tab-btn');
    tabBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        tabBtns.forEach(b => b.classList.remove('active'));
        this.container.querySelectorAll('.awaken-tab-pane').forEach(p => p.classList.remove('active'));
        btn.classList.add('active');
        this.container.querySelector('#' + btn.getAttribute('data-target')).classList.add('active');
        btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      });
    });

    this.container.querySelectorAll('.pill').forEach(btn => {
      btn.addEventListener('click', (e) => this.updateVariant(e.currentTarget));
    });

    this.container.querySelectorAll('.meta-szb-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.selectMetaRingSize(e.currentTarget));
    });
    const moreBtn = this.container.querySelector('.meta-szb-more-btn');
    if (moreBtn) moreBtn.addEventListener('click', () => this.showAllRingSizes());

    const copyBtns = this.container.querySelectorAll('.js-custom-copy-btn');
    copyBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.copyActiveImage(e.currentTarget));
    });
    
    this.container.querySelectorAll('.aw-thumb').forEach(thumb => {
      thumb.addEventListener('click', (e) => this.goToSlide(parseInt(e.currentTarget.dataset.index)));
    });
    
    this.container.querySelectorAll('.awaken-gallery-prev').forEach(btn => btn.addEventListener('click', () => this.scrollMain(-1)));
    this.container.querySelectorAll('.awaken-gallery-next').forEach(btn => btn.addEventListener('click', () => this.scrollMain(1)));
  }

  selectMetaRingSize(btn) {
    const group = btn.closest('.pill-group');
    group.querySelectorAll('.meta-szb-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    
    const val = btn.getAttribute('data-value');
    const inputEl = this.container.querySelector('.aw-meta-ring-input');
    if (inputEl) inputEl.value = val;
    
    const statusEl = this.container.querySelector('.aw-meta-ring-status');
    if (statusEl) statusEl.textContent = val;
  }

  showAllRingSizes() {
    this.container.querySelectorAll('.hidden-size').forEach(btn => btn.classList.remove('hidden-size'));
    const moreBtn = this.container.querySelector('.meta-szb-more-btn');
    if (moreBtn) moreBtn.style.display = 'none';
  }

  // DYNAMIC MULTI-COUNTRY CURRENCY FORMATTER
  formatCurrency(amount) {
    if (isNaN(amount)) return "0";

    // 1. Link directly to global AwakenPricing if active
    if (window.AwakenPricing && window.AwakenPricing.country !== 'IN') {
      const rate = window.AwakenPricing.rate || 1.0;
      const markup = window.AwakenPricing.markup || 1.0;
      const converted = (amount / rate) * markup;
      return Math.round(converted).toLocaleString("en-US");
    }

    // 2. Direct URL param / Config fallback
    const urlParams = new URLSearchParams(window.location.search);
    const country = (urlParams.get('country') || this.config.countryCode || 'IN').toUpperCase();

    if (country !== 'IN') {
      const countryMap = {
        US: { rate: 84.0, markup: 2.00 },
        GB: { rate: 108.0, markup: 1.50 },
        CA: { rate: 62.0, markup: 1.50 },
        AU: { rate: 55.0, markup: 1.50 },
        AE: { rate: 23.0, markup: 1.50 },
        DE: { rate: 92.0, markup: 1.50 },
        SG: { rate: 64.0, markup: 1.50 },
      };
      const cfg = countryMap[country] || { rate: 84.0, markup: 1.00 };
      const converted = (amount / cfg.rate) * cfg.markup;
      return Math.round(converted).toLocaleString("en-US");
    }

    // Domestic (India)
    return Math.round(amount).toLocaleString("en-IN");
  }

  getCurrencySymbol() {
    if (window.AwakenPricing && window.AwakenPricing.symbol && window.AwakenPricing.country !== 'IN') {
      return window.AwakenPricing.symbol;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const country = (urlParams.get('country') || this.config.countryCode || 'IN').toUpperCase();

    const symbols = {
      GB: '£',
      US: '$',
      CA: 'C$',
      AU: 'A$',
      AE: 'AED ',
      DE: '€',
      SG: 'S$',
    };

    return symbols[country] || (country === 'IN' ? '₹' : '$');
  }

  updateBreakdownAndSpecs() {
    let finalPurity = "14KT";
    let finalMetalColor = "YELLOW";
    
    Object.keys(this.currentOptions).forEach(key => {
      let optName = (this.config.optionNames[key] || "").toLowerCase();
      let val = this.currentOptions[key] || "";
      if (!val || optName.includes('size')) return;
      
      const upper = val.toUpperCase();
      const match = upper.match(/(\d+)\s*(K|KT)/);
      if (match) finalPurity = `${match[1]}KT`;
      
      if (upper.includes("WHITE")) finalMetalColor = "WHITE";
      else if (upper.includes("ROSE")) finalMetalColor = "ROSE";
      else if (upper.includes("YELLOW") || upper.includes("GOLD")) finalMetalColor = "YELLOW";
    });

    const variantData = this.config.weightData[finalPurity] || {};
    const wNet = variantData.net || 0;
    const metalP = variantData.metalPrice || 0;
    const making = variantData.making || 0;
    
    const ex = this.config.extraPrices;
    const rawSoliTotal = ex.soli1 + ex.soli2 + ex.soli3;
    const rawSideTotal = ex.smallDiamond + ex.colorStone;
    const rawDiamondTotal = rawSoliTotal + rawSideTotal;
    const rawBaseTotal = metalP + making + rawDiamondTotal;

    const matchedVariant = this.config.productVariants.find(v => {
      return v.option1 === this.currentOptions.option1 &&
             v.option2 === this.currentOptions.option2 &&
             v.option3 === this.currentOptions.option3;
    });

    const shopifyMarketPrice = matchedVariant ? (matchedVariant.price / 100) : (this.config.productVariants[0].price / 100);
    
    let marketMultiplier = 1.0;
    if (rawBaseTotal > 0) {
      marketMultiplier = shopifyMarketPrice / rawBaseTotal;
    }

    const marketMetal = metalP * marketMultiplier;
    const marketMaking = making * marketMultiplier;
    const marketSoliTotal = rawSoliTotal * marketMultiplier;
    const marketSideTotal = rawSideTotal * marketMultiplier;
    const marketDiamondTotal = rawDiamondTotal * marketMultiplier;

    const pTags = this.config.productTags || [];
    const dyn = this.config.dynamicDiscounts || {};
    
    let makingDiscountPct = dyn.making || 0;
    let diamondDiscountPct = dyn.diamond || 0;

    if (makingDiscountPct === 0) {
      if (pTags.includes("AWAKEN4_FREE_MAKING")) makingDiscountPct = 100;
      else if (pTags.includes("AWAKEN4_MAKING") || pTags.includes("AWAKEN4_BOTH")) makingDiscountPct = 35;
    }
    if (diamondDiscountPct === 0) {
      if (pTags.includes("AWAKEN4_DIAMOND") || pTags.includes("AWAKEN4_BOTH")) diamondDiscountPct = 25;
    }

    const makingSavings = marketMaking * (makingDiscountPct / 100);
    const diamondSavings = marketDiamondTotal * (diamondDiscountPct / 100);
    const totalSavings = makingSavings + diamondSavings;

    const discountedMaking = marketMaking - makingSavings;
    const discountedSoliTotal = marketSoliTotal - (marketSoliTotal * (diamondDiscountPct / 100));
    const discountedSideTotal = marketSideTotal - (marketSideTotal * (diamondDiscountPct / 100));

    const finalDynamicTotal = shopifyMarketPrice - totalSavings;

    let percentageSaved = 0;
    if (shopifyMarketPrice > 0 && totalSavings > 0) {
      percentageSaved = Math.round((totalSavings / shopifyMarketPrice) * 100);
    }

    const setText = (selector, txt) => {
      const el = this.container.querySelector(selector);
      if(el) el.textContent = txt;
    };
    const setHtml = (selector, html) => {
      const el = this.container.querySelector(selector);
      if(el) el.innerHTML = html;
    };

    const animatedOffers = this.container.querySelector('#awaken-animated-offers-block');
    if (animatedOffers) {
      animatedOffers.style.display = totalSavings > 0 ? 'none' : 'block';
    }

    setText('.aw-meta-purity', finalPurity + " Gold");
    setText('.aw-meta-weight', (wNet > 0) ? wNet + 'g' : '-g');
    setText('.aw-tab-meta-purity', finalPurity + " Gold");
    setText('.aw-tab-meta-weight', (wNet > 0) ? wNet + 'g' : '-g');
    setText('.aw-pbd-metal-name', `${finalPurity} ${finalMetalColor} GOLD`);
    setText('.aw-pbd-gold-sub', `(approx. ${wNet}g)`);
    
    const sym = this.getCurrencySymbol();
    const symSpan = `<span class="rs">${sym}</span>`;

    const priceSaleEl = this.container.querySelector('.aw-dynamic-price');
    const priceOrigEl = this.container.querySelector('.aw-dynamic-price-original');
    const pricePctEl = this.container.querySelector('.aw-dynamic-price-percent');

    if(priceSaleEl) priceSaleEl.innerHTML = symSpan + this.formatCurrency(finalDynamicTotal);
    
    if(totalSavings > 0) {
       if(priceOrigEl) {
         priceOrigEl.innerHTML = symSpan + this.formatCurrency(shopifyMarketPrice);
         priceOrigEl.style.display = 'inline-block';
       }
       if(pricePctEl) {
         pricePctEl.textContent = `(${percentageSaved}% OFF)`;
         pricePctEl.style.display = 'inline-block';
       }
    } else {
       if(priceOrigEl) priceOrigEl.style.display = 'none';
       if(pricePctEl) pricePctEl.style.display = 'none';
    }

    const discountBox = this.container.querySelector('.aw-discount-box');
    if(discountBox) {
      if(totalSavings > 0) {
        discountBox.style.display = 'block';
        setHtml('.aw-box-sale-price', symSpan + this.formatCurrency(finalDynamicTotal));
        
        const makingRow = this.container.querySelector('.aw-box-making-row');
        if(makingSavings > 0) {
          makingRow.style.display = 'flex';
          const makingTextLabel = makingRow.querySelector('span');
          if (makingTextLabel) makingTextLabel.textContent = makingDiscountPct === 100 ? "100% Off Making Charges" : `${makingDiscountPct}% Off Making Charges`;
          setHtml('.aw-box-making-save', '-' + symSpan + this.formatCurrency(makingSavings));
        } else { makingRow.style.display = 'none'; }
        
        const diaRow = this.container.querySelector('.aw-box-diamond-row');
        if(diamondSavings > 0) {
          diaRow.style.display = 'flex';
          const diaTextLabel = diaRow.querySelector('span');
          if (diaTextLabel) diaTextLabel.textContent = `${diamondDiscountPct}% Off Diamond Value`;
          setHtml('.aw-box-diamond-save', '-' + symSpan + this.formatCurrency(diamondSavings));
        } else { diaRow.style.display = 'none'; }
        
        let dynamicMessage = "Total Savings";
        if (makingSavings > 0 && diamondSavings > 0) dynamicMessage = "Total Savings (Making & Diamonds)";
        else if (makingSavings > 0) dynamicMessage = "Total Savings (Making Charges)";
        else if (diamondSavings > 0) dynamicMessage = "Total Savings (Diamonds)";

        setHtml('.aw-box-discount-msg', dynamicMessage);
        setHtml('.aw-box-total-save', symSpan + this.formatCurrency(totalSavings));
      } else {
        discountBox.style.display = 'none';
      }
    }

    setHtml('.aw-tab-gold-price', symSpan + this.formatCurrency(marketMetal));

    const rowSoli = this.container.querySelector('.aw-row-solitaire');
    const soliTitleEl = this.container.querySelector('.aw-pbd-soli-title');
    if (marketSoliTotal > 0) {
      if(rowSoli) rowSoli.style.display = 'flex';
      if(soliTitleEl) {
        const base = soliTitleEl.getAttribute('data-base') || soliTitleEl.textContent;
        if(!soliTitleEl.hasAttribute('data-base')) soliTitleEl.setAttribute('data-base', base);
        soliTitleEl.innerHTML = diamondSavings > 0 ? `${base} <span style="color:var(--db-text, #118171); font-weight:500; font-size:12px; margin-left:4px;">(${diamondDiscountPct}% OFF)</span>` : base;
      }
      setHtml('.aw-tab-solitaire-price', diamondSavings > 0 ? `<s style="color:var(--muted); font-size:12px; margin-right:6px; font-weight:400;">${symSpan}${this.formatCurrency(marketSoliTotal)}</s> ${symSpan}${this.formatCurrency(discountedSoliTotal)}` : `${symSpan}${this.formatCurrency(marketSoliTotal)}`);
    } else if(rowSoli) { rowSoli.style.display = 'none'; }

    const rowSmallDia = this.container.querySelector('.aw-row-small-dia');
    const sideTitleEl = this.container.querySelector('.aw-pbd-side-title');
    if (marketSideTotal > 0) {
      if(rowSmallDia) rowSmallDia.style.display = 'flex';
      if(sideTitleEl) {
        const base = sideTitleEl.getAttribute('data-base') || sideTitleEl.textContent;
        if(!sideTitleEl.hasAttribute('data-base')) sideTitleEl.setAttribute('data-base', base);
        sideTitleEl.innerHTML = diamondSavings > 0 ? `${base} <span style="color:var(--db-text, #118171); font-weight:500; font-size:12px; margin-left:4px;">(${diamondDiscountPct}% OFF)</span>` : base;
      }
      setHtml('.aw-tab-dia-price', diamondSavings > 0 ? `<s style="color:var(--muted); font-size:14px; margin-right:6px; font-weight:400;">${symSpan}${this.formatCurrency(marketSideTotal)}</s> ${symSpan}${this.formatCurrency(discountedSideTotal)}` : `${symSpan}${this.formatCurrency(marketSideTotal)}`);
    } else if(rowSmallDia) { rowSmallDia.style.display = 'none'; }

    const makeTitleEl = this.container.querySelector('.aw-pbd-make-title');
    if(makeTitleEl) {
      const base = makeTitleEl.getAttribute('data-base') || makeTitleEl.textContent;
      if(!makeTitleEl.hasAttribute('data-base')) makeTitleEl.setAttribute('data-base', base);
      makeTitleEl.innerHTML = makingSavings > 0 ? `${base} <span style="color:var(--db-text, #118171); font-weight:500; font-size:12px; margin-left:4px;">(${makingDiscountPct === 100 ? '100% FREE' : makingDiscountPct + '% OFF'})</span>` : base;
    }
    setHtml('.aw-tab-make-price', makingSavings > 0 ? `<s style="color:var(--muted); font-size:14px; margin-right:6px; font-weight:400;">${symSpan}${this.formatCurrency(marketMaking)}</s> ${symSpan}${this.formatCurrency(discountedMaking)}` : `${symSpan}${this.formatCurrency(marketMaking)}`);

    setHtml('.aw-tab-total-price', symSpan + this.formatCurrency(finalDynamicTotal));

    const tabSaveBlock = this.container.querySelector('#tab-save-block');
    if (tabSaveBlock) {
      if (totalSavings > 0) {
        tabSaveBlock.style.display = 'flex';
        setText('#tab-save-percent', `(${percentageSaved}% OFF)`);
        setHtml('#tab-save-amount', symSpan + this.formatCurrency(totalSavings));
      } else {
        tabSaveBlock.style.display = 'none';
      }
    }

    const stickyPriceDisplay = this.container.querySelector('.aw-sticky-price');
    if(stickyPriceDisplay) {
        if(totalSavings > 0) {
            stickyPriceDisplay.innerHTML = `<s style="color:#888; font-size:2rem; margin-right:6px;">${symSpan}${this.formatCurrency(shopifyMarketPrice)}</s>${symSpan}${this.formatCurrency(finalDynamicTotal)}`;
        } else {
            stickyPriceDisplay.innerHTML = `${symSpan}${this.formatCurrency(finalDynamicTotal)}`;
        }
    }

    const atcWrapper = this.container.querySelector('.aw-atc-button-wrapper');
    const stickyAtcBar = this.container.querySelector('.aw-sticky-atc');
    const waLinkText = this.container.querySelector('.aw-dynamic-wa-link-text');
    const waLinkSub = this.container.querySelector('.aw-dynamic-wa-link-sub');
    
    if (finalDynamicTotal >= 200000) {
      if(atcWrapper) atcWrapper.style.display = "none";
      if(stickyAtcBar) stickyAtcBar.style.display = "none";
      if(waLinkText) waLinkText.textContent = "Price on Request - Chat Now";
      if(waLinkSub) waLinkSub.style.display = "none";
    } else {
      if(atcWrapper) atcWrapper.style.display = "block";
      if(stickyAtcBar) stickyAtcBar.style.display = "flex";
      if(waLinkText) waLinkText.textContent = waLinkText.getAttribute('data-default') || "Chat on WhatsApp";
      if(waLinkSub) waLinkSub.style.display = "block";
    }
  }

  updateVariant(button) {
    const optIdx = button.getAttribute('data-option-index');
    const optVal = button.getAttribute('data-value');

    const siblings = button.parentElement.querySelectorAll('button');
    siblings.forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');

    this.currentOptions[optIdx] = optVal;

    const lbl = button.closest('.pill-group').parentElement.querySelector('.vlabel-modern span');
    if (lbl) lbl.textContent = optVal.toUpperCase();

    this.updateBreakdownAndSpecs();

    const matchedVariant = this.config.productVariants.find(v => {
      return v.option1 === this.currentOptions.option1 &&
             v.option2 === this.currentOptions.option2 &&
             v.option3 === this.currentOptions.option3;
    });

    if (matchedVariant) {
      const vId = this.container.querySelector('.aw-dynamic-variant-id');
      if (vId) vId.value = matchedVariant.id;

      const skuEl = this.container.querySelector('.aw-spec-sku'); 
      if (skuEl && matchedVariant.sku) skuEl.textContent = matchedVariant.sku;

      const btnEnables = ['.aw-dynamic-add-btn', '.aw-dynamic-buy-btn', '.aw-sticky-add-btn', '.aw-sticky-buy-btn'];
      const txtEnables = ['.aw-dynamic-add-text', '.aw-sticky-add-text'];
      
      btnEnables.forEach(sel => {
        const el = this.container.querySelector(sel);
        if(el) {
          el.disabled = !matchedVariant.available;
          if(sel.includes('buy')) el.style.opacity = matchedVariant.available ? "1" : "0.5";
        }
      });
      
      txtEnables.forEach(sel => {
        const el = this.container.querySelector(sel);
        if(el) el.textContent = matchedVariant.available ? "ADD TO CART" : "SOLD OUT";
      });

      const origMediaIdx = this.config.variantMediaMap[matchedVariant.id];
      if (origMediaIdx !== undefined) {
        const idx = this.config.reorderedIndices.indexOf(origMediaIdx);
        if (idx !== -1) this.goToSlide(idx);
      }
      
      if (history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('variant', matchedVariant.id);
        window.history.replaceState({path: url.href}, '', url.href);
      }
    }
  }

  goToSlide(index, behavior = 'smooth') {
    const targetSlide = this.container.querySelector(`.aw-slide[data-index="${index}"]`);
    if (targetSlide) {
      targetSlide.scrollIntoView({ behavior: behavior, block: 'nearest', inline: 'center' });
      this.thumbIdx = index;
    }
  }

  scrollMain(direction) {
    const slides = this.container.querySelectorAll('.aw-slide');
    if(slides.length === 0) return;
    let newIdx = this.thumbIdx + direction;
    if (newIdx < 0) newIdx = slides.length - 1;
    if (newIdx >= slides.length) newIdx = 0;
    this.goToSlide(newIdx);
  }

  initGallery() {
    const mainSlider = this.container.querySelector('.aw-main-slider');
    const thumbTrack = this.container.querySelector('.aw-thumb-slider');
    const thumbs = this.container.querySelectorAll('.aw-thumb');
    
    if (mainSlider && thumbs.length > 0) {
      const obs = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index'));
            this.thumbIdx = index;
            thumbs.forEach(t => t.classList.remove('active'));
            const activeThumb = this.container.querySelector(`.aw-thumb[data-index="${index}"]`);
            if (activeThumb) {
              activeThumb.classList.add('active');
              if (thumbTrack) thumbTrack.scrollTo({ left: activeThumb.offsetLeft - thumbTrack.offsetLeft - 20, behavior: 'smooth' });
            }
          }
        });
      }, { root: mainSlider, threshold: 0.5 });
      this.container.querySelectorAll('.aw-slide').forEach(slide => obs.observe(slide));
    }

    const mainAtcWrapper = this.container.querySelector('.aw-atc-button-wrapper');
    const stickyAtcBar = this.container.querySelector('.aw-sticky-atc');
    
    if (mainAtcWrapper && stickyAtcBar) {
      const stickyObs = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting && entry.boundingClientRect.top < 0) stickyAtcBar.classList.add('visible');
          else stickyAtcBar.classList.remove('visible');
        });
      }, { threshold: 0 }); 
      stickyObs.observe(mainAtcWrapper);
    }

    setTimeout(() => this.goToSlide(this.config.visualStartIndex, 'auto'), 50);
  }

  initTickers() {
    this.container.querySelectorAll('.aw-animated-offers, .aw-sp-ticker-wrap').forEach(wrap => {
      const items = wrap.querySelectorAll(wrap.classList.contains('aw-animated-offers') ? '.aw-offer-item' : '.aw-sp-ticker-item');
      if(items.length <= 1) { if(items[0]) items[0].classList.add('active'); return; }
      let idx = 0; items[idx].classList.add('active');
      setInterval(() => {
        items[idx].classList.remove('active');
        idx = (idx + 1) % items.length;
        items[idx].classList.add('active');
      }, wrap.classList.contains('aw-animated-offers') ? 3500 : 4000);
    });
  }

  async copyActiveImage(btn) {
    const origHTML = btn.innerHTML;
    try {
      if (!navigator.clipboard || !window.ClipboardItem) {
        alert("Copying images is not supported in this browser or requires a secure HTTPS connection.");
        return;
      }
      const activeSlide = this.container.querySelector(`.aw-slide[data-index="${this.thumbIdx}"]`);
      if(!activeSlide) return;
      
      const imgEl = activeSlide.querySelector('img');
      if (!imgEl) { 
        alert('No image found to copy.'); 
        return; 
      }

      btn.innerHTML = '<span style="font-size:12px;">⏳</span>';
      const srcUrl = new URL(imgEl.src, window.location.href);
      srcUrl.searchParams.set('nocache', Date.now());

      const response = await fetch(srcUrl.href, { mode: 'cors' });
      if (!response.ok) throw new Error(`Network response was ${response.status}`);
      const rawBlob = await response.blob();

      const pngBlob = await new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(rawBlob);
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          canvas.getContext('2d').drawImage(img, 0, 0);
          URL.revokeObjectURL(objectUrl);
          
          canvas.toBlob((b) => {
            if (b) resolve(b);
            else reject(new Error("Canvas to PNG conversion failed."));
          }, 'image/png');
        };
        
        img.onerror = () => reject(new Error("Failed to load fetched image into canvas."));
        img.src = objectUrl;
      });

      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': pngBlob })
      ]);

      btn.innerHTML = '<span style="font-size:14px; color: #1a2f31; font-weight:bold;"> ✔ </span>';
      setTimeout(() => { btn.innerHTML = origHTML; }, 2000);

    } catch (err) { 
      console.error('Clipboard copy failed:', err); 
      alert(`Copy failed: ${err.message}. Your CDN may be blocking CORS.`); 
      btn.innerHTML = origHTML; 
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.awaken-section-wrapper').forEach(el => new AwakenProduct(el));
});